import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { FormData } from "../../constants";
import RHFRadioGroup from "src/shared/components/form-fields/RHF-radio-group";
import RHFTextField from "src/shared/components/form-fields/RHF-textfield";
import { ICarBrand, ICarColor } from "src/types/generic";
import { collection, getDocs } from "firebase/firestore";
import { db } from "src/firebase";
import { useTranslation } from "react-i18next";
import countries from "src/constants/country-codes.json";
import CountryFlag from "src/shared/components/country-flag";
import { IAttachmentFile } from "src/shared/components/file-uploader";
import RHFAutocomplete from "src/shared/components/form-fields/RHF-autocomplete";

import PhotoCapturing from "../photo-capture";

const RequestCarWash = ({
  files,
  setFiles,
  carBrands,
  setCarBrands,
  carColors,
  setCarColors,
}: {
  files: IAttachmentFile[];
  setFiles: Function;
  carBrands: ICarBrand[];
  setCarBrands: Function;
  carColors: ICarColor[];
  setCarColors: Function;
}) => {
  const { t, i18n } = useTranslation();
  const [openCamera, setOpenCamera] = useState(false);

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormData>();

  const isSaudiCarPlate = watch("plateType") === "saudi";

  useEffect(() => {
    const fetchCarBrands = async () => {
      const snapshot = await getDocs(collection(db, "car_brands"));

      setCarBrands(
        snapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
    };

    fetchCarBrands();
  }, []);

  // fetch car colors
  useEffect(() => {
    const fetchCarColors = async () => {
      const snapshot = await getDocs(collection(db, "car_colors"));

      setCarColors(
        snapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
    };

    fetchCarColors();
  }, []);

  return (
    <Grid container spacing={2} mt={"10px"}>
      <Grid item xs={12}>
        <RHFTextField
          name="userName"
          label="enterYourName"
          placeholder="enterYourName"
        />
      </Grid>
      <Grid item xs={12}>
        <Grid
          container
          spacing={1}
          sx={{ direction: "ltr !important" }}
          direction={i18n.language === "en" ? "row" : "row-reverse"}
        >
          <Grid item xs={12}>
            <Typography color={"primary"}>{t("phoneNumber")}</Typography>
          </Grid>
          <Grid item xs={5}>
            <FormControl fullWidth error={!!errors.countryCode} size="small">
              <Controller
                name="countryCode"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    renderValue={(value) => {
                      const country = countries.find(
                        (c) => c.dialCode === value,
                      );

                      if (!country) {
                        return "Select Country";
                      }

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexDirection:
                              i18n.language === "en" ? "row" : "row-reverse",
                          }}
                        >
                          <CountryFlag code={country.isoCode} />

                          <Typography dir="ltr">{country.dialCode}</Typography>
                        </Box>
                      );
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem
                        key={country.isoCode}
                        value={country.dialCode}
                        onClick={() => setValue("isoCode", country.isoCode)}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            flexDirection:
                              i18n.language === "en" ? "row" : "row-reverse",
                          }}
                        >
                          <CountryFlag code={country.isoCode} />

                          <Typography dir="ltr">{country.dialCode}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {t(`${errors.countryCode?.message ?? ""}`)}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={7}>
            <RHFTextField
              name="userPhone"
              label=""
              placeholder="phoneNumber"
              inputProps={{
                dir: "ltr",
                style: {
                  textAlign: "left",
                },
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <RHFRadioGroup
          name="plateType"
          options={[
            {
              label: "saudiCarPlate",
              value: "saudi",
            },
            {
              label: "nonSaudiCarPlate",
              value: "non-saudi",
            },
          ]}
        />
      </Grid>

      {isSaudiCarPlate && (
        <Grid item xs={6}>
          <RHFTextField name="plateNum" label="plateNo" placeholder="plateNo" />
        </Grid>
      )}

      <Grid item xs={isSaudiCarPlate ? 6 : 12}>
        <RHFTextField
          name="plateLetters"
          label="plateLetters"
          placeholder="plateLetters"
        />
      </Grid>

      <Grid item xs={6}>
        <RHFAutocomplete
          name="carBrand"
          label="carBrand"
          options={carBrands.map((brand) => ({
            label: i18n.language === "en" ? brand.nameEn : brand.nameAr,
            value: brand.id,
          }))}
        />
      </Grid>

      <Grid item xs={6}>
        <RHFAutocomplete
          name="carColor"
          label="carColor"
          options={carColors.map((color) => ({
            label: i18n.language === "en" ? color.nameEn : color.nameAr,
            value: color.id,
          }))}
        />
      </Grid>

      <Grid item xs={12}>
        <RHFTextField
          name="parkingSlotNum"
          label="parkingSlotNumber"
          placeholder="parkingSlotNumber"
        />
      </Grid>
      <Grid item xs={12}>
        <PhotoCapturing files={files} setFiles={setFiles} />
      </Grid>
    </Grid>
  );
};

export default RequestCarWash;
